'use strict'

import React from 'react'
import { getMessage } from '../../../../common/utils/MessageUtils'
import { runValidation } from './validation'
import { convertConditionToColFormat, parseConditionToAst } from './conditionAst'

/**
 * 登録確認モーダルの説明文（新規 / 更新）
 *
 * @param {{ isNew: boolean; dataKindName: string; chrofyColumnName: string }} params
 * @returns {React.ReactElement}
 */
export function buildRegisterConfirmDescription({ isNew, dataKindName, chrofyColumnName }) {
  const kind = dataKindName || ''
  const column = chrofyColumnName || ''
  const boldKind = <span className="font-bold">{kind}</span>
  const boldColumn = <span className="font-bold">{column}</span>

  if (isNew) {
    return (
      <React.Fragment>
        <div className="text-center">判定ルールを登録します。よろしいですか？</div>
        <div className="pt-5 text-center">
          登録後、汎用マッピング画面（データ分類「{boldKind}」）で
          <br />「{boldColumn}」にルール適用を行い、データ検証を実行してください。
          <br />
        </div>
        <div className="pt-5 text-center">※ルールを適用しない限り、データ取込時に判定結果は自動設定されません。</div>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <div className="text-center">判定ルールを更新します。よろしいですか？</div>
      <div className="pt-5 text-center">
        「{boldColumn}」の判定結果に影響する場合があります。
        <br />
        汎用マッピング画面（データ分類「{boldKind}」）でデータ検証を再実行してください。
      </div>
    </React.Fragment>
  )
}

/**
 * 条件式の(空)は実際の値としては空文字として扱う（API送信用に "" に変換）
 *
 * @param {string} condition
 * @returns {string}
 */
function toActualCondition(condition) {
  return (condition || '').replace(/\(空\)/g, '""')
}

/**
 * バリデーション・条件式パースを行い、IF02089 リクエストを組み立てる
 *
 * @param {object} state
 * @param {{ get: (key: string) => string | null }} searchParams
 * @returns {{
 *       ok: true
 *       request: import('./types').JudgmentRuleRequest
 *       isNew: boolean
 *     }
 *   | {
 *       ok: false
 *       serverErrors: string[]
 *       clientErrors: Record<string, string>
 *     }}
 */
export function prepareJudgmentRuleRegisterRequest(state, searchParams) {
  const { isError, clientErrors, serverErrors } = runValidation(state)

  if (isError) {
    const serverErrorMessages = serverErrors.map((e) => getMessage(e.messageId, ...e.args))
    const clientErrorsResolved = Object.fromEntries(
      Object.entries(clientErrors).map(([k, v]) => [k, getMessage(v.messageId, ...v.args)])
    )
    return { ok: false, serverErrors: serverErrorMessages, clientErrors: clientErrorsResolved }
  }

  const { judgmentRules, elseResult, periodKind } = state
  const invalidConditionIndices = []
  const expressions = []

  for (let i = 0; i < judgmentRules.length; i++) {
    try {
      const actualCondition = toActualCondition(judgmentRules[i].condition)
      const colFormat = convertConditionToColFormat(actualCondition)
      expressions.push(parseConditionToAst(colFormat))
    } catch {
      invalidConditionIndices.push(i + 1)
    }
  }

  if (invalidConditionIndices.length > 0) {
    const conditionNumbers = `条件式${invalidConditionIndices.join(',')}`
    return {
      ok: false,
      serverErrors: [getMessage('E011021', conditionNumbers)],
      clientErrors: {}
    }
  }

  const requestJudgmentRules = judgmentRules.map((rule, i) => ({
    Condition: toActualCondition(rule.condition),
    Value: rule.result,
    ConditionExpression: JSON.stringify(expressions[i])
  }))

  const elseConditionExpression = JSON.stringify({
    type: 'Literal',
    value: true,
    raw: 'true'
  })
  requestJudgmentRules.push({
    Condition: '',
    Value: elseResult || '',
    ConditionExpression: elseConditionExpression
  })

  const ruleId = searchParams.get('id')
  const isNew = searchParams.get('new') === 'true' || !ruleId
  const periodKindForApi =
    periodKind === 'yearly' ? 'year' : periodKind === 'quarter' ? 'quarter' : periodKind === 'monthly' ? 'monthly' : ''

  const request = {
    RuleId: ruleId || '',
    RuleName: state.ruleName,
    ValidStartMonth: state.validStartMonth || '',
    ValidEndMonth: state.validEndMonth || '',
    PeriodKind: periodKindForApi || undefined,
    DataKindName: state.targetCategory,
    DataKindCode: state.targetCategoryCode || '',
    ChrofyColumnName: state.targetField,
    ChrofyColumnCode: state.targetFieldCode || '',
    JudgmentRules: requestJudgmentRules
  }

  return { ok: true, request, isNew }
}
